import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, ClipboardCheck, TrendingUp, Shield, Database, ArrowRight, CheckCircle2, Mail, User, Phone, Briefcase } from 'lucide-react';
import { startSession } from '../utils/api';

// FMiQ Logo Component - Official Logo
const FMiQLogo = ({ className = "h-10", variant = "dark" }) => (
  <svg className={className} viewBox="0 0 180 50" fill="none" xmlns="http://www.w3.org/2000/svg">
    {/* Bar chart graphic - 3 bars with varying heights */}
    <rect x="4" y="22" width="8" height="22" rx="2" fill="#4A90E2"/>
    <rect x="16" y="8" width="8" height="36" rx="2" fill="#7BB1FF"/>
    <rect x="28" y="16" width="8" height="28" rx="2" fill="#4A90E2"/>
    {/* Curved base/foundation */}
    <path d="M2 46 Q20 38 38 46" stroke="#F5A623" strokeWidth="3" fill="none" strokeLinecap="round"/>
    {/* FMiQ text */}
    <text x="48" y="36" fontFamily="Inter, sans-serif" fontSize="30" fontWeight="700" fill={variant === "dark" ? "white" : "#0F172A"}>
      FM<tspan fill="#F5A623">i</tspan>Q
    </text>
  </svg>
);

// FMiQ Logo for light backgrounds
const FMiQLogoLight = ({ className = "h-10" }) => (
  <FMiQLogo className={className} variant="light" />
);

const categories = [
  { icon: Building2, name: 'Asset Readiness', desc: 'Inventory completeness & data quality' },
  { icon: ClipboardCheck, name: 'Maintenance Readiness', desc: 'Work order & PM program maturity' },
  { icon: TrendingUp, name: 'Capital Planning', desc: 'Lifecycle data & forecast capability' },
  { icon: Shield, name: 'Compliance & Risk', desc: 'Inspection tracking & audit readiness' },
  { icon: Database, name: 'Data Governance', desc: 'Standards, ownership & quality' }
];

const portfolioOptions = ['1', '2-10', '11-50', '50+'];
const goalOptions = [
  { value: 'PM', label: 'Improve Preventive Maintenance' },
  { value: 'Capital', label: 'Capital Planning & Budgeting' },
  { value: 'Audit', label: 'Audit & Compliance Readiness' },
  { value: 'Cost', label: 'Cost Reductions' },
  { value: 'Other', label: 'General Assessment' }
];

function LandingPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [errors, setErrors] = useState({});
  const [formStartTime, setFormStartTime] = useState(null);
  const [humanCheck, setHumanCheck] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    contact_name: '',
    job_title: '',
    phone: '',
    org_name: '',
    portfolio_size: '',
    primary_goal: '',
    // Honeypot field - bots will fill this, humans won't see it
    website_url: ''
  });

  const validateForm = () => {
    const newErrors = {};
    
    // Honeypot check - if filled, it's a bot
    if (formData.website_url) {
      console.log('Bot detected via honeypot');
      // Silently reject - don't tell the bot why
      return false;
    }
    
    // Time check - form must take at least 5 seconds to fill
    if (formStartTime) {
      const timeSpent = (Date.now() - formStartTime) / 1000;
      if (timeSpent < 5) {
        console.log('Bot detected via timing:', timeSpent, 'seconds');
        // Silently reject
        return false;
      }
    }
    
    // Human confirmation check
    if (!humanCheck) {
      newErrors.humanCheck = 'Please confirm you are not a robot';
    }
    
    if (!formData.email) {
      newErrors.email = 'Email is required to receive your results';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    
    setLoading(true);
    try {
      // Don't send honeypot field to backend
      const { website_url, ...cleanData } = formData;
      const result = await startSession(cleanData);
      navigate(`/survey/${result.session_id}`);
    } catch (error) {
      console.error('Failed to start session:', error);
      alert('Failed to start survey. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleGetStarted = () => {
    setShowForm(true);
    setFormStartTime(Date.now()); // Start timing when form is shown
    setTimeout(() => {
      document.getElementById('intake-form')?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  return (
    <div className="min-h-screen bg-cloud-grey">
      {/* Hero Section - Deep Navy Background */}
      <div className="bg-deep-navy">
        {/* Header with Logo */}
        <header className="max-w-6xl mx-auto px-4 py-6">
          <FMiQLogo className="h-10" />
        </header>
        
        {/* Hero Content */}
        <div className="max-w-6xl mx-auto px-4 pb-20 pt-8">
          <div className="text-center">
            <div className="inline-flex items-center px-4 py-2 bg-white/10 rounded-full text-sm text-white mb-6" data-testid="hero-badge">
              <CheckCircle2 className="w-4 h-4 mr-2 text-action-amber" />
              <span className="font-inter">Free Assessment • 10 Minutes • Actionable Results</span>
            </div>
            <h1 className="font-inter text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight" data-testid="hero-title">
              FM Operations<br />
              <span className="text-primary-blue">Readiness Survey</span>
            </h1>
            <p className="font-lato text-lg text-slate-grey mb-8 max-w-3xl mx-auto leading-relaxed" data-testid="hero-subtitle">
              Assess your facility management maturity across 5 critical dimensions. 
              Get a defensible scorecard, identify gaps, and receive actionable recommendations.
            </p>
            <button
              onClick={handleGetStarted}
              className="inline-flex items-center justify-center px-8 py-4 bg-action-amber hover:bg-accent-600 text-deep-navy font-inter font-semibold rounded-lg transition-all duration-200 transform hover:scale-105 shadow-lg"
              data-testid="get-started-btn"
            >
              Get Your Free Assessment
              <ArrowRight className="w-5 h-5 ml-2" />
            </button>
            <p className="mt-4 text-sm text-slate-grey font-lato">
              Results delivered instantly to your inbox
            </p>
          </div>
        </div>
        
        {/* Decorative bottom arc */}
        <div className="h-4 bg-gradient-to-r from-primary-blue via-sky-support to-action-amber"></div>
      </div>

      {/* Intake Form - Required for Lead Gen */}
      {showForm && (
        <div id="intake-form" className="bg-white border-b shadow-lg">
          <div className="max-w-2xl mx-auto px-4 py-10">
            <div className="text-center mb-8">
              <h2 className="font-inter text-2xl font-bold text-deep-navy mb-2">Start Your Assessment</h2>
              <p className="font-lato text-slate-grey">Takes about 10 minutes. Get your personalized results report.</p>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="intake-form">
              {/* Email - Required */}
              <div>
                <label className="block text-sm font-inter font-medium text-deep-navy mb-1">
                  Work Email <span className="text-action-amber">*</span>
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-grey" />
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    placeholder="you@company.com"
                    className={`w-full pl-10 pr-4 py-3 border ${errors.email ? 'border-red-500' : 'border-slate-300'} rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-transparent font-lato`}
                    data-testid="email-input"
                  />
                </div>
                {errors.email && <p className="mt-1 text-sm text-red-500 font-lato">{errors.email}</p>}
                <p className="mt-1 text-xs text-slate-grey font-lato">Your results will be sent to this email</p>
              </div>
              
              {/* Portfolio Size */}
              <div>
                <label className="block text-sm font-inter font-medium text-deep-navy mb-2">How many buildings/facilities do you manage?</label>
                <div className="flex flex-wrap gap-2">
                  {portfolioOptions.map(opt => (
                    <button
                      key={opt}
                      type="button"
                      onClick={() => setFormData({...formData, portfolio_size: opt})}
                      className={`px-5 py-3 rounded-lg border-2 transition-all font-inter text-sm ${
                        formData.portfolio_size === opt 
                          ? 'border-primary-blue bg-primary-blue text-white' 
                          : 'border-slate-300 hover:border-primary-blue text-deep-navy'
                      }`}
                      data-testid={`portfolio-${opt}`}
                    >
                      {opt === '1' ? '1' : opt === '2-10' ? '2-10' : opt === '11-50' ? '11-50' : '50+'}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Honeypot field - hidden from humans, bots will fill it */}
              <div style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0, overflow: 'hidden' }} aria-hidden="true">
                <label htmlFor="website_url">Website URL (leave blank)</label>
                <input
                  type="text"
                  id="website_url"
                  name="website_url"
                  value={formData.website_url}
                  onChange={(e) => setFormData({...formData, website_url: e.target.value})}
                  tabIndex={-1}
                  autoComplete="off"
                />
              </div>
              
              {/* Human confirmation checkbox */}
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="human-check"
                  checked={humanCheck}
                  onChange={(e) => setHumanCheck(e.target.checked)}
                  className="mt-1 w-5 h-5 rounded border-slate-300 text-primary-blue focus:ring-primary-blue cursor-pointer"
                  data-testid="human-check"
                />
                <label htmlFor="human-check" className="text-sm text-deep-navy font-lato cursor-pointer select-none">
                  I confirm I'm a facility management professional interested in this assessment
                </label>
              </div>
              {errors.humanCheck && (
                <p className="text-red-500 text-sm font-lato -mt-2">{errors.humanCheck}</p>
              )}
              
              <button
                type="submit"
                disabled={loading}
                className="w-full py-4 bg-primary-blue hover:bg-primary-600 text-white font-inter font-semibold rounded-lg transition-all duration-200 disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="start-survey-btn"
              >
                {loading ? 'Starting...' : 'Start My Assessment'}
                <ArrowRight className="w-5 h-5" />
              </button>
              
              <p className="text-xs text-center text-slate-grey font-lato">
                By continuing, you agree to receive your assessment results and occasional updates from FMiQ. 
                We respect your privacy and won't share your information.
              </p>
            </form>
          </div>
        </div>
      )}

      {/* Categories Overview */}
      <div className="max-w-6xl mx-auto px-4 py-16">
        <h2 className="font-inter text-2xl font-bold text-deep-navy text-center mb-4" data-testid="categories-heading">
          5 Dimensions of FM Readiness
        </h2>
        <p className="font-lato text-slate-grey text-center mb-12 max-w-2xl mx-auto">
          Each category contains 5 evidence-based questions with clear scoring rubrics to ensure consistent, defensible results.
        </p>
        
        <div className="grid md:grid-cols-5 gap-4">
          {categories.map((cat, idx) => {
            const Icon = cat.icon;
            return (
              <div key={idx} className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 text-center hover:shadow-md hover:border-primary-300 transition-all duration-200" data-testid={`category-card-${idx}`}>
                <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon className="w-6 h-6 text-primary-blue" />
                </div>
                <h3 className="font-inter font-semibold text-deep-navy mb-2">{cat.name}</h3>
                <p className="font-lato text-sm text-slate-grey">{cat.desc}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* What You Get */}
      <div className="bg-white py-16">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="font-inter text-2xl font-bold text-deep-navy text-center mb-12" data-testid="benefits-heading">
            What You'll Get
          </h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-cloud-grey rounded-xl p-6 border border-slate-200">
              <div className="w-12 h-12 bg-primary-blue/10 rounded-lg flex items-center justify-center mb-4">
                <TrendingUp className="w-6 h-6 text-primary-blue" />
              </div>
              <h3 className="font-inter font-semibold text-lg text-deep-navy mb-2">Readiness Scorecard</h3>
              <p className="font-lato text-slate-grey">Scores across all 5 categories with evidence strength indicators showing confidence in your assessment.</p>
            </div>
            <div className="bg-cloud-grey rounded-xl p-6 border border-slate-200">
              <div className="w-12 h-12 bg-action-amber/10 rounded-lg flex items-center justify-center mb-4">
                <Shield className="w-6 h-6 text-action-amber" />
              </div>
              <h3 className="font-inter font-semibold text-lg text-deep-navy mb-2">Priority Gap Analysis</h3>
              <p className="font-lato text-slate-grey">Top 5 improvement areas ranked by impact, plus recommended next steps tailored to your scores.</p>
            </div>
            <div className="bg-cloud-grey rounded-xl p-6 border border-slate-200">
              <div className="w-12 h-12 bg-sky-support/10 rounded-lg flex items-center justify-center mb-4">
                <Mail className="w-6 h-6 text-sky-support" />
              </div>
              <h3 className="font-inter font-semibold text-lg text-deep-navy mb-2">Email Report</h3>
              <p className="font-lato text-slate-grey">Professional report delivered to your inbox with executive summary and actionable recommendations.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Footer CTA */}
      <div className="max-w-4xl mx-auto px-4 py-16 text-center">
        <h2 className="font-inter text-2xl font-bold text-deep-navy mb-4">
          Ready to Assess Your FM Operations?
        </h2>
        <p className="font-lato text-slate-grey mb-8">
          Takes about 10 minutes. Get instant results with service recommendations.
        </p>
        <button
          onClick={handleGetStarted}
          className="inline-flex items-center justify-center px-8 py-4 bg-primary-blue hover:bg-primary-600 text-white font-inter font-semibold rounded-lg transition-all duration-200 transform hover:scale-105"
          data-testid="footer-start-btn"
        >
          Get Your Free Assessment
          <ArrowRight className="w-5 h-5 ml-2" />
        </button>
      </div>

      {/* Footer */}
      <footer className="bg-deep-navy py-8">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between">
            <FMiQLogo className="h-8 mb-4 md:mb-0" />
            <p className="font-lato text-slate-grey text-sm">© 2026 FMiQ. Facility Management Intelligence.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default LandingPage;
